import React, {
  ReactNode,
  forwardRef,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ActionId,
  ActionImpl,
  KBarProvider,
  KBarPortal,
  KBarPositioner,
  KBarAnimator,
  KBarSearch,
  useMatches,
  KBarResults,
  useKBar,
  Action,
} from "kbar";
import { useHistory } from "react-router-dom";
import styles from "./PageSearch.module.scss";
import { AppContext } from "../../../../context/app";
import MembersSearch from "./MemberSearch";
import { Icons, Text } from "@pushpress/shared-components";
import { pageSearchActions } from "./PageSearch.utils";
import { Command } from "kbar/lib/action/Command";

type PageSearchProps = {
  children: ReactNode;
};

function PageSearch({ children }: PageSearchProps) {
  const { spa } = useContext(AppContext);
  const [search, setSearch] = useState("");
  const history = useHistory();

  const routes = useMemo(() => {
    return pageSearchActions().map((action, key) => {
      return {
        id: String(key + 1),
        name: action.name,
        section: "navigation",
        icon: action.icon,
        keywords: "",
        perform: () => {
          if (spa && action.isReactPage) {
            history.push(action.targetPage);
          } else {
            window.location.pathname = action.targetPage;
          }
        },
      };
    });
  }, [history, spa]);

  return (
    <KBarProvider actions={routes}>
      <PageSearchListener />
      {children}
      <KBarPortal>
        <KBarPositioner className={styles.container}>
          <KBarAnimator className={styles.animatorStyle}>
            <MembersSearch search={search} />
            <KBarSearch
              className={styles.searchStyle}
              defaultPlaceholder="Search for a person or a page"
              onChange={(e) => setSearch(e.target.value)}
              data-testid="kbar-search"
            />
            <RenderResults search={search} />
          </KBarAnimator>
        </KBarPositioner>
      </KBarPortal>
    </KBarProvider>
  );
}

function PageSearchListener() {
  const { query } = useKBar();

  useEffect(() => {
    const handlePageSearch = () => {
      query.toggle();
    };

    window.addEventListener("PageSearch", handlePageSearch);

    // Cleanup listener on component unmount
    return () => {
      window.removeEventListener("PageSearch", handlePageSearch);
    };
  }, [query]);

  return <></>;
}

function RenderResults({ search }: { search: string }) {
  const { results, rootActionId } = useMatches();

  // Add search people item
  const items: ((Action & { command?: Command }) | string)[] = [...results];
  const peopleIndex = items.findIndex((item) => item === "people");
  if (search && peopleIndex !== -1) {
    items.splice(peopleIndex + 1, 0, {
      id: "search-people",
      name: search,
      command: {
        perform: () => {
          window.location.pathname = `/members/search/${search}`;
        },
      },
      icon: (
        <div className={styles.searchIcon}>
          <Icons.Search />
        </div>
      ),
    });
  }

  return (
    <KBarResults
      items={items}
      onRender={({ item, active }) =>
        typeof item === "string" ? (
          <div className={styles.groupNameStyle}>{item}</div>
        ) : (
          <ResultItem
            action={item}
            active={active}
            currentRootActionId={rootActionId}
          />
        )
      }
    />
  );
}

const ResultItem = forwardRef(
  (
    {
      action,
      active,
    }: {
      action: ActionImpl;
      active: boolean;
      currentRootActionId: ActionId;
    },
    ref: React.Ref<HTMLDivElement>,
  ) => {
    return (
      <div
        ref={ref}
        className={`${styles.resultContainer} ${
          active ? styles.resultContainerActive : ""
        }`}
      >
        <div className={styles.resultContent}>
          {action.icon && action.icon}
          <div className={styles.resultLine}>
            <div>
              <Text variant="body-lg" component="span">
                {action.name}
              </Text>
            </div>
            {action.subtitle && (
              <span className={styles.resultSubtitle}>{action.subtitle}</span>
            )}
          </div>
        </div>
      </div>
    );
  },
);

ResultItem.displayName = "ResultItem";

export default PageSearch;
